"use strict";
// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
Object.defineProperty(exports, "__esModule", { value: true });
exports.PackageService = exports.AgentPermission = exports.AppScope = void 0;
const tslib_1 = require("tslib");
const hooks_1 = require("@feathersjs/hooks");
const teamsfx_api_1 = require("@microsoft/teamsfx-api");
const adm_zip_1 = tslib_1.__importDefault(require("adm-zip"));
const form_data_1 = tslib_1.__importDefault(require("form-data"));
const fs_extra_1 = tslib_1.__importDefault(require("fs-extra"));
const https_1 = tslib_1.__importDefault(require("https"));
const strip_bom_1 = tslib_1.__importDefault(require("strip-bom"));
const globalVars_1 = require("../../common/globalVars");
const localizeUtils_1 = require("../../common/localizeUtils");
const projectTypeChecker_1 = require("../../common/projectTypeChecker");
const telemetry_1 = require("../../common/telemetry");
const utils_1 = require("../../common/utils");
const wrappedAxiosClient_1 = require("../../common/wrappedAxiosClient");
const common_1 = require("../../error/common");
const types_1 = require("../../error/types");
const constants_1 = require("./constants");
const errors_1 = require("./errors");
const constants_2 = require("../../common/constants");
const M365ErrorSource = "M365";
const M365ErrorComponent = "PackageService";
var AppScope;
(function (AppScope) {
    AppScope["Personal"] = "Personal";
    AppScope["Shared"] = "Shared";
    AppScope["Tenant"] = "Tenant";
})(AppScope || (exports.AppScope = AppScope = {}));
exports.AgentPermission = {
    name: "Agent",
    owner: "Owner",
    type: "M365",
};
// Call m365 service for package CRUD
class PackageService {
    static sharedInstance;
    axiosInstance;
    initEndpoint;
    logger;
    static GetSharedInstance() {
        if (!PackageService.sharedInstance) {
            PackageService.sharedInstance = new PackageService((0, constants_2.getResourceServiceEndpoint)(constants_2.ResourceServiceType.MOS3), globalVars_1.TOOLS.logProvider);
        }
        return PackageService.sharedInstance;
    }
    constructor(endpoint, logger) {
        this.axiosInstance = wrappedAxiosClient_1.WrappedAxiosClient.create({
            timeout: 30000,
            httpsAgent: new https_1.default.Agent({ keepAlive: true }),
        });
        this.initEndpoint = endpoint;
        this.logger = logger;
    }
    async withNetworkRetry(fn, retries = 3) {
        for (let i = 0; i < retries; i++) {
            try {
                return await fn();
            }
            catch (e) {
                // Only retry on network/TLS errors (no HTTP response means transport-level failure)
                if (e.response || i === retries - 1) {
                    throw e;
                }
                this.logger?.warning(`Request failed with ${e.code ?? e.message}, retrying (${i + 1}/${retries})...`);
                await (0, utils_1.waitSeconds)(1);
            }
        }
        throw new Error("Unexpected: retry loop exited");
    }
    async getTitleServiceUrl(token) {
        try {
            try {
                new URL(this.initEndpoint);
            }
            catch (_) {
                throw new Error("Invalid URL. Mis-configuration SIDELOADING_SERVICE_ENDPOINT.");
            }
            const envInfo = await this.withNetworkRetry(() => this.axiosInstance.get("/config/v1/environment", {
                baseURL: this.initEndpoint,
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }));
            this.logger?.debug(JSON.stringify(envInfo.data));
            new URL(envInfo.data.titlesServiceUrl);
            return envInfo.data.titlesServiceUrl;
        }
        catch (error) {
            this.logger?.error(`Get ServiceUrl failed. ${error.message}`);
            throw error;
        }
    }
    async sideLoadXmlManifest(token, manifestPath) {
        try {
            const data = await fs_extra_1.default.readFile(manifestPath);
            const content = new form_data_1.default();
            content.append("package", data);
            content.append("culture", "en-us");
            const serviceUrl = await this.getTitleServiceUrl(token);
            const uploadResponse = await this.axiosInstance.post("/dev/v1/users/packages/addins", content.getBuffer(), {
                baseURL: serviceUrl,
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/xml",
                },
            });
            if (uploadResponse.status === 200) {
                const titleId = uploadResponse.data.titleId;
                const appId = uploadResponse.data.appId;
                this.logger?.info(`TitleId: ${titleId}`);
                this.logger?.info(`AppId: ${appId}`);
                this.logger?.verbose("Sideloading done.");
                return [titleId, appId];
            }
            else if (uploadResponse.status === 202) {
                const statusId = uploadResponse.data.statusId;
                this.logger?.debug(`Acquiring package with statusId: ${statusId} ...`);
                do {
                    const statusResponse = await this.axiosInstance.get(`/dev/v1/users/packages/status/${statusId}`, {
                        baseURL: serviceUrl,
                        headers: { Authorization: `Bearer ${token}` },
                    });
                    const resCode = statusResponse.status;
                    this.logger?.debug(`Package status: ${resCode} ...`);
                    if (resCode === 200) {
                        const titleId = statusResponse.data.titleId;
                        const appId = statusResponse.data.appId;
                        this.logger?.info(`TitleId: ${titleId}`);
                        this.logger?.info(`AppId: ${appId}`);
                        this.logger?.verbose("Sideloading done.");
                        return [titleId, appId];
                    }
                    else {
                        await (0, utils_1.waitSeconds)(2);
                    }
                } while (true);
            }
            else {
                throw new Error(`Unknown response code: ${uploadResponse.status}}`);
            }
        }
        catch (error) {
            // this.logger?.error("Sideloading failed.");
            if (error.response) {
                // this.logger?.error(JSON.stringify(error.response.data));
                error = this.traceError(error);
            }
            else {
                // this.logger?.error(error.message);
            }
            throw (0, common_1.assembleError)(error, M365ErrorSource);
        }
    }
    async sideLoading(token, packagePath, appScope = AppScope.Personal) {
        const manifest = this.getManifestFromZip(packagePath);
        if (!manifest) {
            throw new Error("Invalid app package zip. manifest.json is missing");
        }
        const isDelcarativeAgentApp = (0, projectTypeChecker_1.IsDeclarativeAgentManifest)(manifest);
        if (isDelcarativeAgentApp) {
            const res = await this.sideLoadingV2(token, packagePath, appScope);
            let shareLink = "";
            if (appScope.toLowerCase() === AppScope.Shared.toLowerCase()) {
                shareLink = await this.getShareLink(token, res[0]);
            }
            (0, telemetry_1.sendTelemetryEvent)(telemetry_1.Component.core, telemetry_1.TelemetryEvent.MosSideloadEnd, {
                [telemetry_1.TelemetryProperty.MosTitleId]: res[0],
                [telemetry_1.TelemetryProperty.MosAppId]: res[1],
                [telemetry_1.TelemetryProperty.IsDeclarativeAgent]: isDelcarativeAgentApp.toString(),
            });
            return [res[0], res[1], shareLink];
        }
        else {
            const res = await this.sideLoadingV1(token, packagePath);
            (0, telemetry_1.sendTelemetryEvent)(telemetry_1.Component.core, telemetry_1.TelemetryEvent.MosSideloadEnd, {
                [telemetry_1.TelemetryProperty.MosTitleId]: res[0],
                [telemetry_1.TelemetryProperty.MosAppId]: res[1],
                [telemetry_1.TelemetryProperty.IsDeclarativeAgent]: isDelcarativeAgentApp.toString(),
            });
            return [res[0], res[1], ""];
        }
    }
    // Side loading using Builder API
    async sideLoadingV2(token, manifestPath, appScope) {
        try {
            this.checkZip(manifestPath);
            const data = await fs_extra_1.default.readFile(manifestPath);
            const content = new form_data_1.default();
            content.append("package", data);
            content.append("info", JSON.stringify({ builderName: "TeamsToolKit" }));
            const serviceUrl = await this.getTitleServiceUrl(token);
            this.logger?.debug(`"Uploading package with sideLoading V2 in ${appScope} scope ..."`);
            const uploadHeaders = content.getHeaders();
            uploadHeaders["Authorization"] = `Bearer ${token}`;
            const uploadResponse = await this.withNetworkRetry(() => this.axiosInstance.post("/builder/v1/users/packages", content, {
                baseURL: serviceUrl,
                headers: uploadHeaders,
                params: {
                    scope: appScope,
                    shouldBlock: true,
                },
            }));
            if (uploadResponse.status === 200 || uploadResponse.status === 201) {
                const titleId = uploadResponse.data.titlePreview.titleId;
                const appId = uploadResponse.data.titlePreview.appId;
                this.logger?.info(`TitleId: ${titleId}`);
                this.logger?.info(`AppId: ${appId}`);
                this.logger?.verbose("Sideloading done.");
                return [titleId, appId];
            }
            const statusId = uploadResponse.data.statusId;
            this.logger?.debug(`Acquiring package with statusId: ${statusId} ...`);
            do {
                const statusResponse = await this.axiosInstance.get(`/builder/v1/users/packages/status/${statusId}`, {
                    baseURL: serviceUrl,
                    headers: { Authorization: `Bearer ${token}` },
                });
                const resCode = statusResponse.status;
                this.logger?.debug(`Package status: ${resCode} ...`);
                if (resCode === 200) {
                    const titleId = statusResponse.data.titleId;
                    const appId = statusResponse.data.appId;
                    this.logger?.info(`TitleId: ${titleId}`);
                    this.logger?.info(`AppId: ${appId}`);
                    this.logger?.verbose("Sideloading done.");
                    return [titleId, appId];
                }
                else {
                    await (0, utils_1.waitSeconds)(7);
                }
            } while (true);
        }
        catch (error) {
            if (error.response) {
                error = this.traceError(error);
            }
            const err = (0, common_1.assembleError)(error, M365ErrorSource);
            if (err instanceof teamsfx_api_1.UserError) {
                err.helpLink = constants_1.M365HelpLink;
            }
            throw err;
        }
    }
    async sideLoadingV1(token, manifestPath) {
        try {
            this.checkZip(manifestPath);
            const data = await fs_extra_1.default.readFile(manifestPath);
            const content = new form_data_1.default();
            content.append("package", data);
            const serviceUrl = await this.getTitleServiceUrl(token);
            this.logger?.debug("Uploading package with sideLoading V1 ...");
            const uploadHeaders = content.getHeaders();
            uploadHeaders["Authorization"] = `Bearer ${token}`;
            const uploadResponse = await this.withNetworkRetry(() => this.axiosInstance.post("/dev/v1/users/packages", content.getBuffer(), {
                baseURL: serviceUrl,
                headers: uploadHeaders,
            }));
            const operationId = uploadResponse.data.operationId;
            this.logger?.debug(`Package uploaded. OperationId: ${operationId}`);
            this.logger?.verbose("Acquiring package ...");
            const acquireResponse = await this.axiosInstance.post("/dev/v1/users/packages/acquisitions", {
                operationId: operationId,
            }, {
                baseURL: serviceUrl,
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            const statusId = acquireResponse.data.statusId;
            this.logger?.debug(`Acquiring package with statusId: ${statusId} ...`);
            do {
                const statusResponse = await this.axiosInstance.get(`/dev/v1/users/packages/status/${statusId}`, {
                    baseURL: serviceUrl,
                    headers: { Authorization: `Bearer ${token}` },
                });
                const resCode = statusResponse.status;
                this.logger?.debug(`Package status: ${resCode} ...`);
                if (resCode === 200) {
                    const titleId = statusResponse.data.titleId;
                    const appId = statusResponse.data.appId;
                    this.logger?.info(`TitleId: ${titleId}`);
                    this.logger?.info(`AppId: ${appId}`);
                    this.logger?.verbose("Sideloading done.");
                    return [titleId, appId];
                }
                else {
                    await (0, utils_1.waitSeconds)(7);
                }
            } while (true);
        }
        catch (error) {
            // this.logger?.error("Sideloading failed.");
            if (error.response) {
                // this.logger?.error(JSON.stringify(error.response.data));
                error = this.traceError(error);
            }
            else {
                // this.logger?.error(error.message);
            }
            throw (0, common_1.assembleError)(error, M365ErrorSource);
        }
    }
    async getShareLink(token, titleId) {
        const serviceUrl = await this.getTitleServiceUrl(token);
        try {
            const resp = await this.axiosInstance.get(`/marketplace/v1/users/titles/${titleId}/sharingInfo`, {
                baseURL: serviceUrl,
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            return resp.data.unifiedStoreLink;
        }
        catch (error) {
            if (error.response) {
                error = this.traceError(error);
            }
            throw (0, common_1.assembleError)(error, M365ErrorSource);
        }
    }
    async getLaunchInfoByManifestId(token, manifestId) {
        try {
            const serviceUrl = await this.getTitleServiceUrl(token);
            this.logger?.verbose(`Getting LaunchInfo with ManifestId ${manifestId} ...`);
            const launchInfo = await this.axiosInstance.post("/catalog/v1/users/titles/launchInfo", {
                Id: manifestId,
                IdType: "ManifestId",
                Filter: {
                    SupportedElementTypes: [
                        // "Extensions", // Extensions require ClientDetails to be determined later
                        "OfficeAddIns",
                        "ExchangeAddIns",
                        "FirstPartyPages",
                        "Dynamics",
                        "AAD",
                        "LineOfBusiness",
                        "StaticTabs",
                        "ComposeExtensions",
                        "Bots",
                        "GraphConnector",
                        "ConfigurableTabs",
                        "Activities",
                        "MeetingExtensionDefinition",
                        "OpenAIPlugins",
                        "Gpts",
                        "DeclarativeCopilots",
                        "Plugins",
                    ],
                },
            }, {
                baseURL: serviceUrl,
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            this.logger?.debug(JSON.stringify(launchInfo.data));
            return launchInfo.data;
        }
        catch (error) {
            this.logger?.error("Get LaunchInfo failed.");
            if (error.response) {
                if (error.response.status === 404) {
                    throw new errors_1.NotExtendedToM365Error(M365ErrorSource);
                }
                error = this.traceError(error);
            }
            throw (0, common_1.assembleError)(error, M365ErrorSource);
        }
    }
    async retrieveTitleId(token, manifestId) {
        const launchInfo = await this.getLaunchInfoByManifestId(token, manifestId);
        const titleId = launchInfo.acquisition?.titleId?.id ??
            launchInfo.acquisition?.titleId;
        this.logger?.debug(`TitleId: ${titleId}`);
        return titleId;
    }
    async retrieveAppId(token, manifestId) {
        const launchInfo = await this.getLaunchInfoByManifestId(token, manifestId);
        const appId = launchInfo.acquisition?.appId;
        this.logger?.debug(`AppId: ${appId}`);
        return appId;
    }
    async unacquire(token, titleId) {
        try {
            const serviceUrl = await this.getTitleServiceUrl(token);
            this.logger?.verbose(`Unacquiring package with TitleId ${titleId} ...`);
            await this.axiosInstance.delete(`/catalog/v1/users/acquisitions/${titleId}`, {
                baseURL: serviceUrl,
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            try {
                await this.axiosInstance.delete(`/builder/v1/users/titles/${titleId}`, {
                    baseURL: serviceUrl,
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
            }
            catch (error) {
                if (error.response && error.response.status === 404) {
                    this.logger?.debug(`TitleId ${titleId} not found, skip deleting.`);
                }
                else {
                    throw error;
                }
            }
            this.logger?.verbose("Unacquiring done.");
        }
        catch (error) {
            if (error.response) {
                error = this.traceError(error);
            }
            throw (0, common_1.assembleError)(error, M365ErrorSource);
        }
    }
    async getLaunchInfoByTitleId(token, titleId) {
        try {
            const serviceUrl = await this.getTitleServiceUrl(token);
            this.logger?.verbose(`Getting LaunchInfo with TitleId ${titleId} ...`);
            const launchInfo = await this.axiosInstance.get(`/catalog/v1/users/titles/${titleId}/launchInfo`, {
                baseURL: serviceUrl,
                params: {
                    SupportedElementTypes: 
                    // eslint-disable-next-line no-secrets/no-secrets
                    "Extensions,OfficeAddIns,ExchangeAddIns,FirstPartyPages,Dynamics,AAD,LineOfBusiness,StaticTabs,ComposeExtensions,Bots,GraphConnector,ConfigurableTabs,Activities,MeetingExtensionDefinition,OpenAIPlugins,Gpts,DeclarativeCopilots,Plugins",
                },
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            this.logger?.info(JSON.stringify(launchInfo.data));
            return launchInfo.data;
        }
        catch (error) {
            if (error.response) {
                error = this.traceError(error);
            }
            throw (0, common_1.assembleError)(error, M365ErrorSource);
        }
    }
    async getActiveExperiences(token, ensureUpToDate = false) {
        try {
            const serviceUrl = await this.getTitleServiceUrl(token);
            this.logger?.debug(`Get active experiences from service URL ${serviceUrl} ...`);
            // users/experiences is deprecating, using users/uitypes instead
            let response = await this.axiosInstance.get("/catalog/v1/users/uitypes", {
                baseURL: serviceUrl,
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            let status = response.status;
            let activeExperiences = response.data?.activeExperiences;
            const nextInterval = response.data?.nextInterval ?? -1;
            this.logger?.debug(`(${status}) Active experiences: ${JSON.stringify(activeExperiences)}`);
            // Short nextInterval means cache is refreshing
            if (ensureUpToDate && nextInterval > 0 && nextInterval < 10) {
                this.logger?.debug(`Active experiences is refreshing, wait for ${nextInterval} seconds.`);
                await (0, utils_1.waitSeconds)(nextInterval);
                response = await this.axiosInstance.get("/catalog/v1/users/uitypes", {
                    baseURL: serviceUrl,
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                status = response.status;
                activeExperiences = response.data?.activeExperiences;
                this.logger?.debug(`(${status}) Active experiences: ${JSON.stringify(activeExperiences)}`);
            }
            return activeExperiences;
        }
        catch (error) {
            if (error.response) {
                error = this.traceError(error);
            }
            throw (0, common_1.assembleError)(error, M365ErrorSource);
        }
    }
    async removePermission(token, titleId, user) {
        try {
            const appRes = await this.previewApp(token, titleId);
            if (appRes.isErr()) {
                return (0, teamsfx_api_1.err)(appRes.error);
            }
            const owners = appRes.value.owners;
            if (!this.isExistingUser(owners, user)) {
                return (0, teamsfx_api_1.ok)(undefined);
            }
            const newOwners = owners.filter((owner) => owner.entityId !== user.aadId);
            const serviceUrl = await this.getTitleServiceUrl(token);
            this.logger?.verbose(`Removing permission to user ${user.displayName} ...`);
            await this.axiosInstance.put(`/builder/v1/users/titles/${titleId}/owners?idType=TitleId`, {
                Owners: newOwners,
            }, {
                baseURL: serviceUrl,
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });
            return (0, teamsfx_api_1.ok)(undefined);
        }
        catch (error) {
            if (error.response) {
                error = this.traceError(error);
            }
            return (0, teamsfx_api_1.err)((0, common_1.assembleError)(error, M365ErrorSource));
        }
    }
    async addOwner(token, titleId, user) {
        try {
            const appRes = await this.previewApp(token, titleId);
            if (appRes.isErr()) {
                return (0, teamsfx_api_1.err)(appRes.error);
            }
            const owners = appRes.value.owners;
            if (this.isExistingUser(owners, user)) {
                return (0, teamsfx_api_1.ok)(undefined);
            }
            const newOwners = owners.map((owner) => {
                return {
                    EntityId: owner.entityId,
                    EntityType: "User",
                };
            });
            newOwners.push({
                EntityId: user.aadId,
                EntityType: "User",
            });
            const serviceUrl = await this.getTitleServiceUrl(token);
            this.logger?.verbose(`Granting permission to user ${user.displayName} ...`);
            await this.axiosInstance.put(`/builder/v1/users/titles/${titleId}/owners?idType=TitleId`, {
                Owners: newOwners,
            }, {
                baseURL: serviceUrl,
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });
            return (0, teamsfx_api_1.ok)(undefined);
        }
        catch (error) {
            if (error.response) {
                error = this.traceError(error);
            }
            return (0, teamsfx_api_1.err)((0, common_1.assembleError)(error, M365ErrorSource));
        }
    }
    async shareWithTenant(token, titleId) {
        try {
            this.logger?.verbose(`Change shared scope of agent to tenant with titleId: ${titleId} ...`);
            const serviceUrl = await this.getTitleServiceUrl(token);
            await this.axiosInstance.post(`/builder/v1/users/titles/${titleId}/allowed?idType=TitleId`, {
                EntityCollection: {
                    ForAllUsers: true,
                    Entities: [],
                },
            }, {
                baseURL: serviceUrl,
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });
            return (0, teamsfx_api_1.ok)(undefined);
        }
        catch (error) {
            if (error.response) {
                error = this.traceError(error);
            }
            return (0, teamsfx_api_1.err)((0, common_1.assembleError)(error, M365ErrorSource));
        }
    }
    async getSharedUsers(token, titleId) {
        try {
            this.logger?.verbose(`Getting shared users with titleId: ${titleId} ...`);
            const serviceUrl = await this.getTitleServiceUrl(token);
            const response = await this.axiosInstance.get(`/builder/v1/users/titles/${titleId}/allowed?idType=TitleId`, {
                baseURL: serviceUrl,
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            const entityCollection = response.data.entityCollection;
            if (entityCollection.entities === undefined) {
                return (0, teamsfx_api_1.ok)([]);
            }
            return (0, teamsfx_api_1.ok)(entityCollection.entities);
        }
        catch (error) {
            if (error.response) {
                error = this.traceError(error);
            }
            return (0, teamsfx_api_1.err)((0, common_1.assembleError)(error, M365ErrorSource));
        }
    }
    // This will overwrite existing entity list
    async shareWithUsers(token, entities, titleId, appId) {
        try {
            this.logger?.verbose(`Adding shared users to app with titleId: ${titleId} ...`);
            const serviceUrl = await this.getTitleServiceUrl(token);
            await this.axiosInstance.post(`/builder/v1/users/titles/${appId ?? titleId}/allowed?idType=${appId ? "AppId" : "TitleId"}`, {
                EntityCollection: {
                    ForAllUsers: false,
                    Entities: entities,
                },
            }, {
                baseURL: serviceUrl,
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });
            return (0, teamsfx_api_1.ok)(undefined);
        }
        catch (error) {
            if (error.response) {
                error = this.traceError(error);
            }
            return (0, teamsfx_api_1.err)((0, common_1.assembleError)(error, M365ErrorSource));
        }
    }
    async unshare(token, titleId) {
        try {
            this.logger?.verbose(`Removing shared users from app with titleId: ${titleId} ...`);
            const serviceUrl = await this.getTitleServiceUrl(token);
            await this.axiosInstance.delete(`/builder/v1/users/titles/${titleId}/allowed?idType=TitleId`, {
                baseURL: serviceUrl,
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });
            return (0, teamsfx_api_1.ok)(undefined);
        }
        catch (error) {
            if (error.response) {
                error = this.traceError(error);
            }
            return (0, teamsfx_api_1.err)((0, common_1.assembleError)(error, M365ErrorSource));
        }
    }
    async previewApp(token, titleId) {
        try {
            const serviceUrl = await this.getTitleServiceUrl(token);
            const response = await this.axiosInstance.get(`/marketplace/v1/users/titles/${titleId}/preview?idType=TitleId`, {
                baseURL: serviceUrl,
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            return (0, teamsfx_api_1.ok)(response.data);
        }
        catch (error) {
            if (error.response) {
                error = this.traceError(error);
            }
            return (0, teamsfx_api_1.err)((0, common_1.assembleError)(error, M365ErrorSource));
        }
    }
    async getCopilotStatus(token, ensureUpToDate = false) {
        try {
            const activeExperiences = await this.getActiveExperiences(token, ensureUpToDate);
            const copilotAllowed = activeExperiences == undefined ? undefined : activeExperiences.includes("CopilotTeams");
            (0, telemetry_1.sendTelemetryEvent)(telemetry_1.Component.core, telemetry_1.TelemetryEvent.CheckCopilot, {
                [telemetry_1.TelemetryProperty.IsCopilotAllowed]: copilotAllowed?.toString() ?? "undefined",
            });
            return copilotAllowed;
        }
        catch (error) {
            (0, telemetry_1.sendTelemetryErrorEvent)(telemetry_1.Component.core, telemetry_1.TelemetryEvent.CheckCopilot, new teamsfx_api_1.SystemError({
                error,
                source: M365ErrorSource,
                message: error.message ?? "Failed to get copilot status.",
                categories: [types_1.ErrorCategory.External],
            }), {
                [telemetry_1.TelemetryProperty.CheckCopilotTracingId]: `${error.response?.headers?.traceresponse ??
                    error.innerError?.response?.headers?.traceresponse ??
                    ""}`,
            });
            return undefined;
        }
    }
    isExistingUser(owners, user) {
        if (owners.length === 0) {
            return false;
        }
        // check entityid and aad id
        for (const owner of owners) {
            if (owner.entityId === user.aadId) {
                return true;
            }
        }
        return false;
    }
    traceError(error) {
        // add error details and trace to message
        const tracingId = (error.response.headers?.traceresponse ?? "");
        const originalMessage = error.message;
        const innerError = error.response.data?.error ||
            error.response.data.Error || { code: "", message: "" };
        const finalMessage = `${originalMessage} (tracingId: ${tracingId}) ${innerError.Code}: ${innerError.Message} `;
        error.message = finalMessage;
        // HTTP 400 as user error due to invalid input
        if (error.response?.status === 400) {
            error = new teamsfx_api_1.UserError({
                name: "PackageServiceError",
                error,
                source: M365ErrorSource,
                message: finalMessage,
            });
        }
        else if (error.response.status === 403 &&
            error.response.data.Error.Message ==
                "User does not have access to upload advanced Copilot apps.") {
            error = new teamsfx_api_1.UserError({
                name: "PackageServiceError",
                error,
                source: M365ErrorSource,
                message: (0, localizeUtils_1.getDefaultString)("error.m365.SharedScopeAdvancedDADisabled", constants_1.advancedDASettingUrl),
                displayMessage: (0, localizeUtils_1.getLocalizedString)("error.m365.SharedScopeAdvancedDADisabled", constants_1.advancedDASettingUrl),
            });
        }
        return error;
    }
    checkZip(path) {
        try {
            const zip = new adm_zip_1.default(path, {});
            zip.getEntries();
        }
        catch (error) {
            this.logger?.debug(`Invalid input zip ${path}. ${error.message}`);
            this.logger?.warning(`Please make sure input path is a valid app package zip. ${path}`);
        }
    }
    getManifestFromZip(path) {
        const zip = new adm_zip_1.default(path);
        const manifestEntry = zip.getEntry("manifest.json");
        if (!manifestEntry) {
            return undefined;
        }
        let manifestContent = manifestEntry.getData().toString("utf8");
        manifestContent = (0, strip_bom_1.default)(manifestContent);
        return JSON.parse(manifestContent);
    }
}
exports.PackageService = PackageService;
tslib_1.__decorate([
    (0, hooks_1.hooks)([(0, globalVars_1.ErrorContextMW)({ source: M365ErrorSource, component: M365ErrorComponent })]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String]),
    tslib_1.__metadata("design:returntype", Promise)
], PackageService.prototype, "getTitleServiceUrl", null);
tslib_1.__decorate([
    (0, hooks_1.hooks)([(0, globalVars_1.ErrorContextMW)({ source: M365ErrorSource, component: M365ErrorComponent })]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, String]),
    tslib_1.__metadata("design:returntype", Promise)
], PackageService.prototype, "sideLoadXmlManifest", null);
tslib_1.__decorate([
    (0, hooks_1.hooks)([(0, globalVars_1.ErrorContextMW)({ source: M365ErrorSource, component: M365ErrorComponent })]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, String, Object]),
    tslib_1.__metadata("design:returntype", Promise)
], PackageService.prototype, "sideLoading", null);
tslib_1.__decorate([
    (0, hooks_1.hooks)([(0, globalVars_1.ErrorContextMW)({ source: M365ErrorSource, component: M365ErrorComponent })]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, String, String]),
    tslib_1.__metadata("design:returntype", Promise)
], PackageService.prototype, "sideLoadingV2", null);
tslib_1.__decorate([
    (0, hooks_1.hooks)([(0, globalVars_1.ErrorContextMW)({ source: M365ErrorSource, component: M365ErrorComponent })]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, String]),
    tslib_1.__metadata("design:returntype", Promise)
], PackageService.prototype, "sideLoadingV1", null);
tslib_1.__decorate([
    (0, hooks_1.hooks)([(0, globalVars_1.ErrorContextMW)({ source: M365ErrorSource, component: M365ErrorComponent })]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, String]),
    tslib_1.__metadata("design:returntype", Promise)
], PackageService.prototype, "getShareLink", null);
tslib_1.__decorate([
    (0, hooks_1.hooks)([(0, globalVars_1.ErrorContextMW)({ source: M365ErrorSource, component: M365ErrorComponent })]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, String]),
    tslib_1.__metadata("design:returntype", Promise)
], PackageService.prototype, "getLaunchInfoByManifestId", null);
tslib_1.__decorate([
    (0, hooks_1.hooks)([(0, globalVars_1.ErrorContextMW)({ source: M365ErrorSource, component: M365ErrorComponent })]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, String]),
    tslib_1.__metadata("design:returntype", Promise)
], PackageService.prototype, "retrieveTitleId", null);
tslib_1.__decorate([
    (0, hooks_1.hooks)([(0, globalVars_1.ErrorContextMW)({ source: M365ErrorSource, component: M365ErrorComponent })]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, String]),
    tslib_1.__metadata("design:returntype", Promise)
], PackageService.prototype, "unacquire", null);
tslib_1.__decorate([
    (0, hooks_1.hooks)([(0, globalVars_1.ErrorContextMW)({ source: M365ErrorSource, component: M365ErrorComponent })]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, String]),
    tslib_1.__metadata("design:returntype", Promise)
], PackageService.prototype, "getLaunchInfoByTitleId", null);
tslib_1.__decorate([
    (0, hooks_1.hooks)([(0, globalVars_1.ErrorContextMW)({ source: M365ErrorSource, component: M365ErrorComponent })]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, Object]),
    tslib_1.__metadata("design:returntype", Promise)
], PackageService.prototype, "getActiveExperiences", null);
tslib_1.__decorate([
    (0, hooks_1.hooks)([(0, globalVars_1.ErrorContextMW)({ source: M365ErrorSource, component: M365ErrorComponent })]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, String, Object]),
    tslib_1.__metadata("design:returntype", Promise)
], PackageService.prototype, "removePermission", null);
tslib_1.__decorate([
    (0, hooks_1.hooks)([(0, globalVars_1.ErrorContextMW)({ source: M365ErrorSource, component: M365ErrorComponent })]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, String, Object]),
    tslib_1.__metadata("design:returntype", Promise)
], PackageService.prototype, "addOwner", null);
tslib_1.__decorate([
    (0, hooks_1.hooks)([(0, globalVars_1.ErrorContextMW)({ source: M365ErrorSource, component: M365ErrorComponent })]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, String]),
    tslib_1.__metadata("design:returntype", Promise)
], PackageService.prototype, "shareWithTenant", null);
tslib_1.__decorate([
    (0, hooks_1.hooks)([(0, globalVars_1.ErrorContextMW)({ source: M365ErrorSource, component: M365ErrorComponent })]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, String]),
    tslib_1.__metadata("design:returntype", Promise)
], PackageService.prototype, "getSharedUsers", null);
tslib_1.__decorate([
    (0, hooks_1.hooks)([(0, globalVars_1.ErrorContextMW)({ source: M365ErrorSource, component: M365ErrorComponent })]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, Array, String, String]),
    tslib_1.__metadata("design:returntype", Promise)
], PackageService.prototype, "shareWithUsers", null);
tslib_1.__decorate([
    (0, hooks_1.hooks)([(0, globalVars_1.ErrorContextMW)({ source: M365ErrorSource, component: M365ErrorComponent })]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, String]),
    tslib_1.__metadata("design:returntype", Promise)
], PackageService.prototype, "unshare", null);
tslib_1.__decorate([
    (0, hooks_1.hooks)([(0, globalVars_1.ErrorContextMW)({ source: M365ErrorSource, component: M365ErrorComponent })]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, String]),
    tslib_1.__metadata("design:returntype", Promise)
], PackageService.prototype, "previewApp", null);
//# sourceMappingURL=packageService.js.map